import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { advertiserOffers, audienceItems } from '@/mocks/advertisersData';

const FORM_URL = 'https://readdy.ai/api/form/d8j8b6kcl43d0bibf70g';

export default function Advertisers() {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: '', company: '', email: '', phone: '', offer: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (status === 'loading') return;
    setStatus('loading');
    try {
      const fd = new URLSearchParams();
      fd.append('name', form.name);
      fd.append('company', form.company);
      fd.append('email', form.email);
      fd.append('phone', form.phone);
      fd.append('offer', form.offer);
      fd.append('message', form.message);
      await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: fd.toString(),
      });
      setStatus('success');
      setForm({ name: '', company: '', email: '', phone: '', offer: '', message: '' });
    } catch {
      setStatus('error');
    }
  };

  return (
    <main className="min-h-screen bg-background-50">
      <Navbar />

      {/* Hero */}
      <section className="pt-24 md:pt-32 pb-12 md:pb-16 bg-primary-900">
        <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
          <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 leading-tight max-w-4xl mx-auto">
            {t('advertisers.title')}
          </h1>
          <p className="text-sm md:text-base text-background-50/70 mt-4 max-w-2xl mx-auto">
            {t('advertisers.subtitle')}
          </p>
        </div>
      </section>

      {/* Audience */}
      <section className="py-12 md:py-16 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">{t('advertisers.audience')}</h2>
            <p className="text-sm text-foreground-500 mt-2">{t('advertisers.audienceDesc')}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {audienceItems.map((item, idx) => (
              <div key={idx} className="bg-background-100 border border-background-200/70 rounded-2xl p-5 flex items-start gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0">
                  <i className="ri-check-line text-accent-600"></i>
                </div>
                <span className="text-sm text-foreground-700">{t(item)}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Offers */}
      <section className="py-12 md:py-16 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">{t('advertisers.offers')}</h2>
            <p className="text-sm text-foreground-500 mt-2">{t('advertisers.offersDesc')}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {advertiserOffers.map((offer) => (
              <div key={offer.id} className="bg-background-50 rounded-2xl p-5 md:p-6 border border-background-200/70 transition-all duration-300 hover:border-accent-200/50">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-100 mb-4">
                  <i className={`${offer.icon} text-xl text-accent-600`}></i>
                </div>
                <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-2">{t(offer.titleKey)}</h3>
                <p className="text-sm text-foreground-500">{t(offer.descKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="py-12 md:py-16 bg-background-50">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <div className="text-center mb-8">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">{t('advertisers.form.title')}</h2>
            <p className="text-sm text-foreground-500 mt-2">{t('advertisers.contact')} <a href="mailto:contact@tennisclubdouala.com" className="text-primary-600 hover:underline cursor-pointer">contact@tennisclubdouala.com</a></p>
          </div>

          <form data-readdy-form onSubmit={handleSubmit} className="bg-background-100 rounded-2xl p-6 md:p-8 border border-background-200/70 space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-1.5">{t('advertisers.form.name')}</label>
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-foreground-200 text-foreground-900 text-sm focus:outline-none focus:border-accent-400 transition-colors"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-1.5">{t('advertisers.form.company')}</label>
                <input
                  type="text"
                  name="company"
                  value={form.company}
                  onChange={(e) => setForm({ ...form, company: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-foreground-200 text-foreground-900 text-sm focus:outline-none focus:border-accent-400 transition-colors"
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-1.5">{t('advertisers.form.email')}</label>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-foreground-200 text-foreground-900 text-sm focus:outline-none focus:border-accent-400 transition-colors"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-1.5">{t('advertisers.form.phone')}</label>
                <input
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-foreground-200 text-foreground-900 text-sm focus:outline-none focus:border-accent-400 transition-colors"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">{t('advertisers.form.offer')}</label>
              <select
                name="offer"
                value={form.offer}
                onChange={(e) => setForm({ ...form, offer: e.target.value })}
                className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-foreground-200 text-foreground-900 text-sm focus:outline-none focus:border-accent-400 transition-colors"
                required
              >
                <option value="">--</option>
                {advertiserOffers.map((o) => (
                  <option key={o.id} value={t(o.titleKey)}>{t(o.titleKey)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">{t('advertisers.form.message')}</label>
              <textarea
                name="message"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                rows={4}
                maxLength={500}
                className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-foreground-200 text-foreground-900 text-sm focus:outline-none focus:border-accent-400 transition-colors resize-none"
                required
              ></textarea>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="submit"
                disabled={status === 'loading'}
                className="px-8 py-3 bg-primary-500 text-background-50 font-semibold text-sm rounded-full hover:bg-primary-600 transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-60"
              >
                {status === 'loading' ? '...' : t('advertisers.form.cta')}
              </button>
              {status === 'success' && (
                <span className="text-sm text-green-600 font-medium">Message envoyé avec succès !</span>
              )}
              {status === 'error' && (
                <span className="text-sm text-red-500 font-medium">Erreur, veuillez réessayer.</span>
              )}
            </div>
          </form>
        </div>
      </section>

      <Footer />

      {/* Floating Buttons */}
      <div className="fixed bottom-6 right-6 z-40 flex flex-col gap-3">
        <a
          href="tel:+237687696969"
          className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-full bg-accent-500 text-white hover:bg-accent-600 transition-all duration-300 hover:scale-110 cursor-pointer shadow-lg"
          aria-label="Appeler"
        >
          <i className="ri-phone-fill text-2xl md:text-3xl"></i>
        </a>
        <a
          href="https://wa.me/237687696969"
          target="_blank"
          rel="noopener noreferrer"
          className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-full bg-[#25D366] text-white hover:bg-[#22c55e] transition-all duration-300 hover:scale-110 cursor-pointer"
          aria-label="Contact WhatsApp"
        >
          <i className="ri-whatsapp-line text-2xl md:text-3xl"></i>
        </a>
      </div>
    </main>
  );
}